import React, {
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import './HeroAnim.css';

// const HERO_NAMES = [
//   'akwadala', 'abracaska', 'blopanneau', 'bouftou_r', 'bulbisou', 'bwak',
//   'deluxe', 'dodge', 'dofus_forgelave', 'dofus_ivoire', 'dofus_pourpre', 'dofus_sylvestre',
//   'dragon_cochon', 'fallanster', 'ganymede', 'gargandyas', 'gelano', 'gloursonne',
//   'guerre', 'harebourg', 'ilyzaelle', 'jahash', 'kokulte', 'kralano', 'limbes',
//   'meulou', 'padgref', 'parure_vautour', 'piou', 'prygen', 'rasboulaire',
//   'roissingue', 'servitude', 'solomonk', 'tofu_fou', 'ventrublion', 'vents', 'voile',
// ];

const HERO_NAMES = [
  'dofus_ebene', 'dofus_ivoire', 'dofus_pourpre', 'dofus_turquoise', 'dofus_vulbis',
  'dofus_emeraude', 'dofus_ocre'
];

const POOL = HERO_NAMES.map((n) => `/img/hero/${n}.webp`);

const EDGES = ['top', 'bottom', 'left', 'right'];
const DURATION_MIN = 105;
const DURATION_MAX = 145;
const MARGIN = 0.08;
const ROTATION_MIN = 90;
const ROTATION_MAX = 720;
const FADE_IN_END = 0.15;
const FADE_OUT_START = 0.75;
/** Smaller sprites get a lower opacity multiplier (linear between min/max size) */
const SIZE_OPACITY_MUL_MIN = 1;
const SIZE_OPACITY_MUL_MAX = 1;
/** Share of items that appear mid-flight on first paint (startTime backdated) */
const MID_FLIGHT_RATIO = 0.5;
const MID_FLIGHT_PROGRESS_MIN = 0.46;
const MID_FLIGHT_PROGRESS_MAX = 0.72;
const HERO_ITEM_SIZE_MULTIPLIER = 0.4;
/** 0 = no size randomness (all equal), 1 = current/default randomness */
const HERO_ITEM_RANDOMNESS = 0;
const HERO_ITEM_COUNT_MIN = 5;
const HERO_ITEM_COUNT_MAX = 5;
const HERO_ITEM_COUNT_BASE_WIDTH = 320;
const HERO_ITEM_COUNT_WIDTH_STEP = 80;

function getViewportWidth() {
  if (typeof window === 'undefined') return 1024;
  return window.innerWidth;
}

function getDpr() {
  if (typeof window === 'undefined') return 1;
  return Math.max(1, window.devicePixelRatio || 1);
}

/** Fewer floating sprites on narrow viewports; scales smoothly with width. */
function heroAnimTargetCount(w) {
  const scaled = Math.round(HERO_ITEM_COUNT_MIN + (w - HERO_ITEM_COUNT_BASE_WIDTH) / HERO_ITEM_COUNT_WIDTH_STEP);
  return Math.min(HERO_ITEM_COUNT_MAX, Math.max(HERO_ITEM_COUNT_MIN, scaled));
}

function scaleRange(base, scale) {
  return {
    min: Math.round(base.min * scale),
    max: Math.round(base.max * scale),
  };
}

/** Mobile-first range with extra boost on high-DPR phones. */
function heroAnimSizeRange(w, dpr = 1) {
  let base;
  if (w < 400) base = { min: 52, max: 136 };
  else if (w < 560) base = { min: 58, max: 152 };
  else if (w < 720) base = { min: 64, max: 172 };
  else if (w < 960) base = { min: 66, max: 188 };
  else base = { min: 74, max: 210 };

  let dprScale = 1;
  if (w <= 720 && dpr >= 3) dprScale = 1.22;
  else if (w <= 720 && dpr >= 2) dprScale = 1.12;

  const scaled = scaleRange(base, dprScale * HERO_ITEM_SIZE_MULTIPLIER);
  const midpoint = (scaled.min + scaled.max) / 2;
  const halfSpan = ((scaled.max - scaled.min) / 2) * HERO_ITEM_RANDOMNESS;
  return {
    min: Math.round(midpoint - halfSpan),
    max: Math.round(midpoint + halfSpan),
  };
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function randomIn(min, max) {
  return min + Math.random() * (max - min);
}

function pickTwoDifferentEdges() {
  const a = pick(EDGES);
  let b = pick(EDGES);
  while (b === a) b = pick(EDGES);
  return [a, b];
}

function positionOnEdge(edge, margin) {
  const m = margin * 100;
  switch (edge) {
    case 'left':
      return { x: `${-m}vw`, y: `${randomIn(0, 100)}vh` };
    case 'right':
      return { x: `${100 + m}vw`, y: `${randomIn(0, 100)}vh` };
    case 'top':
      return { x: `${randomIn(0, 100)}vw`, y: `${-m}vh` };
    case 'bottom':
      return { x: `${randomIn(0, 100)}vw`, y: `${100 + m}vh` };
    default:
      return { x: '0vw', y: '0vh' };
  }
}

function parseVwVh(str) {
  return parseFloat(String(str), 10) || 0;
}

function getFade(progress) {
  if (progress <= FADE_IN_END) return progress / FADE_IN_END;
  if (progress >= FADE_OUT_START) return Math.max(0, (1 - progress) / (1 - FADE_OUT_START));
  return 1;
}

function sizeOpacityMul(sizePx, minPx, maxPx) {
  const span = maxPx - minPx;
  const t = span > 0 ? (sizePx - minPx) / span : 1;
  const u = Math.min(1, Math.max(0, t));
  return SIZE_OPACITY_MUL_MIN + u * (SIZE_OPACITY_MUL_MAX - SIZE_OPACITY_MUL_MIN);
}

function createItem(id, opts = {}, range, srcOverride = null) {
  const { startProgress = 0 } = opts;
  const r = range || { min: 70, max: 200 };
  const [startEdge, endEdge] = pickTwoDifferentEdges();
  const start = positionOnEdge(startEdge, MARGIN);
  const end = positionOnEdge(endEdge, MARGIN);
  const duration = randomIn(DURATION_MIN, DURATION_MAX);
  const rotation = randomIn(ROTATION_MIN, ROTATION_MAX);
  const sizePx = Math.round(randomIn(r.min, r.max));
  const p = Math.min(0.999, Math.max(0, startProgress));
  const startTime = Date.now() - p * duration * 1000;
  return {
    id,
    src: srcOverride || pick(POOL),
    sizePx,
    sizeMinPx: r.min,
    sizeMaxPx: r.max,
    startX: start.x,
    startY: start.y,
    endX: end.x,
    endY: end.y,
    startXVw: parseVwVh(start.x),
    startYVh: parseVwVh(start.y),
    endXVw: parseVwVh(end.x),
    endYVh: parseVwVh(end.y),
    duration,
    rotation,
    startTime,
  };
}

function pickAvailableSource(itemsInPlay) {
  const used = new Set(itemsInPlay.map((it) => it.src));
  const available = POOL.filter((src) => !used.has(src));
  if (available.length === 0) return pick(POOL);
  return pick(available);
}

function itemFrame(it, now) {
  const progress = (now - it.startTime) / (it.duration * 1000);
  if (progress >= 1) return { done: true };
  const x = it.startXVw + (it.endXVw - it.startXVw) * progress;
  const y = it.startYVh + (it.endYVh - it.startYVh) * progress;
  const rot = it.rotation * progress;
  const opacity = getFade(progress) * sizeOpacityMul(it.sizePx, it.sizeMinPx, it.sizeMaxPx);
  return { done: false, x, y, rot, opacity };
}

function applyFrame(el, frame) {
  if (!el || frame.done) return;
  el.style.transform = `translate(${frame.x}vw, ${frame.y}vh) rotate(${frame.rot}deg)`;
  el.style.opacity = String(frame.opacity);
}

function nextId() {
  return `hero-anim-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export default function HeroAnim() {
  const [vw, setVw] = useState(getViewportWidth);
  const [dpr, setDpr] = useState(getDpr);
  const targetCount = useMemo(
    () => Math.min(heroAnimTargetCount(vw), POOL.length),
    [vw],
  );
  const sizeRange = useMemo(() => heroAnimSizeRange(vw, dpr), [vw, dpr]);
  const sizeRangeRef = useRef(sizeRange);
  sizeRangeRef.current = sizeRange;

  const [items, setItems] = useState(() => {
    const w = getViewportWidth();
    const pixelRatio = getDpr();
    const n = heroAnimTargetCount(w);
    const range = heroAnimSizeRange(w, pixelRatio);
    const count = Math.min(n, POOL.length);
    const initialItems = [];
    for (let i = 0; i < count; i += 1) {
      const mid = Math.random() < MID_FLIGHT_RATIO;
      initialItems.push(createItem(
        nextId(),
        mid ? { startProgress: randomIn(MID_FLIGHT_PROGRESS_MIN, MID_FLIGHT_PROGRESS_MAX) } : {},
        range,
        pickAvailableSource(initialItems),
      ));
    }
    return initialItems;
  });

  useEffect(() => {
    const onResize = () => {
      setVw(window.innerWidth);
      setDpr(getDpr());
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    setItems((prev) => {
      if (prev.length === targetCount) return prev;
      if (prev.length > targetCount) return prev.slice(0, targetCount);
      const range = sizeRangeRef.current;
      const toAdd = targetCount - prev.length;
      const nextItems = [...prev];
      const extra = Array.from({ length: toAdd }, () => {
        const mid = Math.random() < MID_FLIGHT_RATIO;
        const item = createItem(
          nextId(),
          mid ? { startProgress: randomIn(MID_FLIGHT_PROGRESS_MIN, MID_FLIGHT_PROGRESS_MAX) } : {},
          range,
          pickAvailableSource(nextItems),
        );
        nextItems.push(item);
        return item;
      });
      return [...prev, ...extra];
    });
  }, [targetCount]);

  const replaceItem = useCallback((id) => {
    setItems((prev) => {
      const next = prev.filter((it) => it.id !== id);
      next.push(
        createItem(nextId(), {}, sizeRangeRef.current, pickAvailableSource(next)),
      );
      return next;
    });
  }, []);

  const itemRefs = useRef({});
  const itemsRef = useRef(items);
  const replaceItemRef = useRef(replaceItem);
  itemsRef.current = items;
  replaceItemRef.current = replaceItem;

  useLayoutEffect(() => {
    const now = Date.now();
    items.forEach((it) => {
      const el = itemRefs.current[it.id];
      if (!el) return;
      const frame = itemFrame(it, now);
      if (frame.done) return;
      applyFrame(el, frame);
    });
  }, [items]);

  useEffect(() => {
    let rafId;
    function tick() {
      const now = Date.now();
      const list = itemsRef.current;
      list.forEach((it) => {
        const el = itemRefs.current[it.id];
        if (!el) return;
        const frame = itemFrame(it, now);
        if (frame.done) {
          replaceItemRef.current(it.id);
          return;
        }
        applyFrame(el, frame);
      });
      rafId = requestAnimationFrame(tick);
    }
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);

  return (
    <div className="hero-anim" aria-hidden="true">
      {items.map((it) => (
        <div
          key={it.id}
          ref={(el) => {
            if (el) itemRefs.current[it.id] = el;
            else delete itemRefs.current[it.id];
          }}
          className="hero-anim-item-wrap"
          style={{
            width: it.sizePx,
            height: it.sizePx,
          }}
        >
          <img
            src={it.src}
            alt=""
            className="hero-anim-item"
            draggable={false}
          />
        </div>
      ))}
    </div>
  );
}
